// ============================================
// AdvancedInsightsPanel Component
// Overall Information panel with stats and customers overview
// ============================================

import { cn } from '../../../utils/cn';
import { useI18n } from '../../../i18n';
import { useTweenedNumbers } from '../../../hooks/useTweenedNumbers';
import {
  QUICK_PERIODS,
  QUICK_PERIOD_LABEL_KEYS,
  isQuickPeriod,
  type QuickPeriod,
} from '../../../utils/periodRange';

// ============================================
// Types
// ============================================

export interface InsightsData {
  stockCount: number;
  suppliers?: number;
  customers?: number;
  orders?: number;
  firstTimeCustomers?: number;
  returnCustomers?: number;
  firstTimeChange?: number;
  returnChange?: number;
}

interface AdvancedInsightsPanelProps {
  data: InsightsData;
  isLoading?: boolean;
  className?: string;
  /**
   * Bug #32: kỳ đang chọn ở dropdown "Customers Overview".
   * State nằm ở DashboardPage vì chính trang mới là nơi gọi API — giữ ở đây thì
   * chọn gì cũng không ai đọc tới (đúng hiện tượng người dùng báo).
   */
  period: QuickPeriod;
  onPeriodChange: (period: QuickPeriod) => void;
  /**
   * Đang tải dữ liệu của kỳ MỚI trong khi vẫn hiển thị số của kỳ cũ
   * (`isFetching && isPlaceholderData`). Chỉ làm mờ nhẹ khối "Customers
   * Overview", KHÔNG dựng skeleton — skeleton chỉ dành cho lần tải đầu tiên.
   */
  isRefreshing?: boolean;
}

// ============================================
// Donut Chart (SVG-based, two rings)
// ============================================

function TwoRingDonut({
  firstTimePercent,
  returnPercent,
}: {
  firstTimePercent: number;
  returnPercent: number;
}) {
  // Chuyển động nằm TRỌN trong component này: nơi gọi cứ truyền phần trăm cuối
  // cùng, còn việc đi từ giá trị đang hiển thị sang giá trị mới là của hook.
  // Lần đầu mount: bắt đầu từ 0 -> hai vòng fill dần. Đổi kỳ khi đã có dữ liệu:
  // morph từ giá trị hiện tại, KHÔNG vẽ lại từ 0.
  // Hook đứng đầu component, không có return sớm nào phía trên — rules-of-hooks.
  const [firstTimeDrawn, returnDrawn] = useTweenedNumbers([firstTimePercent, returnPercent]);

  const size = 140;
  const cx = size / 2;
  const cy = size / 2;

  // Outer ring: First Time (orange-red)
  const outerR = 58;
  const outerStroke = 14;

  // Inner ring: Return (teal)
  const innerR = 38;
  const innerStroke = 12;

  // Cung tròn bắt đầu từ 12 giờ, chạy theo chiều kim đồng hồ. `d` được tính lại
  // mỗi frame từ phần trăm đã nội suy nên không cần stroke-dash gì cả.
  function arcPath(r: number, percent: number) {
    const endDeg = (percent / 100) * 360;
    const toXY = (deg: number) => {
      const rad = (deg - 90) * (Math.PI / 180);
      return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };
    const s = toXY(0);
    const e = toXY(endDeg);
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${endDeg > 180 ? 1 : 0} 1 ${e.x} ${e.y}`;
  }

  // Hàm thường (gọi trực tiếp), KHÔNG khai báo thành component lồng trong: một
  // component định nghĩa trong thân render sẽ có identity mới mỗi lần render và
  // bị remount ở những lần re-render không liên quan.
  function renderRing(r: number, strokeWidth: number, color: string, percent: number) {
    if (percent <= 0) return null;
    // 100% -> cung khép kín, điểm đầu trùng điểm cuối nên <path> arc suy biến;
    // dùng <circle> cho trọn vòng.
    if (percent >= 99.999) {
      return (
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
        />
      );
    }
    return (
      <path
        d={arcPath(r, percent)}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Outer track — NỀN, không tween */}
      <circle
        cx={cx} cy={cy} r={outerR}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={outerStroke}
      />
      {/* Outer progress (First Time - orange-red) */}
      {renderRing(outerR, outerStroke, '#E8472A', firstTimeDrawn)}
      {/* Inner track — NỀN, không tween */}
      <circle
        cx={cx} cy={cy} r={innerR}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={innerStroke}
      />
      {/* Inner progress (Return - teal) */}
      {renderRing(innerR, innerStroke, '#0D9488', returnDrawn)}
    </svg>
  );
}

// ============================================
// Stat Card (top section)
// ============================================

function OverallStatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-[#E0E0E0] bg-[#F9FAFB] px-4 py-2">
      <div className="flex h-9 w-9 items-center justify-center">
        {icon}
      </div>
      <span className="text-xs text-[#9E9E9E]">{label}</span>
      <span className="text-lg font-extrabold text-[#1A1A1A]">{value.toLocaleString()}</span>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function AdvancedInsightsPanel({
  data,
  isLoading = false,
  className,
  period,
  onPeriodChange,
  isRefreshing = false,
}: AdvancedInsightsPanelProps) {
  const { t } = useI18n();

  // Mờ nhẹ trong lúc tải nền. Không chặn tương tác, không spinner.
  const dimClass = cn('transition-opacity', isRefreshing && 'opacity-60');

  // Chỉ chạy ở lần tải ĐẦU TIÊN: từ lần đổi kỳ trở đi query dùng
  // `keepPreviousData` nên vẫn còn dữ liệu cũ, `isLoading` không bật lại.
  if (isLoading) {
    return (
      <div className={cn('h-[440px] animate-pulse rounded-xl bg-slate-200', className)} />
    );
  }

  // Bug #25: KHÔNG fallback số mock (2487/5500/3500/25/21...) — cửa hàng chưa có
  // khách thì chart phải là 0%, không tự vẽ 50%/61% gây hiểu nhầm có số liệu thật.
  const suppliers = data.suppliers ?? 0;
  const customers = data.customers ?? 0;
  const orders = data.orders ?? 0;
  const firstTime = data.firstTimeCustomers ?? 0;
  const returnCust = data.returnCustomers ?? 0;
  const firstTimeChange = data.firstTimeChange ?? 0;
  const returnChange = data.returnChange ?? 0;

  const total = firstTime + returnCust;
  const hasCustomerData = total > 0;
  const firstTimePercent = hasCustomerData ? (firstTime / total) * 100 : 0;
  const returnPercent = hasCustomerData ? (returnCust / total) * 100 : 0;

  function formatK(n: number) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  }

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm overflow-hidden', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Top section */}
      <div className="p-4">
        <h3 className="mb-3 font-sans text-lg font-bold text-[#1A1A1A]">
          {t('overallInformation')}
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <OverallStatCard
            label={t('suppliers')}
            value={suppliers}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clipPath="url(#clip0_2363_14247)">
                  <path d="M23.9456 10.9688L22.6331 7.6875C22.5218 7.40988 22.3296 7.17209 22.0816 7.00494C21.8335 6.83778 21.541 6.74897 21.2419 6.75H18V6C18 5.80109 17.921 5.61032 17.7803 5.46967C17.6397 5.32902 17.4489 5.25 17.25 5.25H3C2.60218 5.25 2.22064 5.40804 1.93934 5.68934C1.65804 5.97064 1.5 6.35218 1.5 6.75V17.25C1.5 17.6478 1.65804 18.0294 1.93934 18.3107C2.22064 18.592 2.60218 18.75 3 18.75H4.59375C4.75898 19.3953 5.13428 19.9673 5.66048 20.3757C6.18669 20.7842 6.83387 21.0059 7.5 21.0059C8.16613 21.0059 8.81331 20.7842 9.33952 20.3757C9.86572 19.9673 10.241 19.3953 10.4062 18.75H15.0938C15.259 19.3953 15.6343 19.9673 16.1605 20.3757C16.6867 20.7842 17.3339 21.0059 18 21.0059C18.6661 21.0059 19.3133 20.7842 19.8395 20.3757C20.3657 19.9673 20.741 19.3953 20.9062 18.75H22.5C22.8978 18.75 23.2794 18.592 23.5607 18.3107C23.842 18.0294 24 17.6478 24 17.25V11.25C24.0004 11.1536 23.9819 11.0581 23.9456 10.9688ZM18 8.25H21.2419L22.1419 10.5H18V8.25ZM3 6.75H16.5V12.75H3V6.75ZM7.5 19.5C7.20333 19.5 6.91332 19.412 6.66665 19.2472C6.41997 19.0824 6.22771 18.8481 6.11418 18.574C6.00065 18.2999 5.97094 17.9983 6.02882 17.7074C6.0867 17.4164 6.22956 17.1491 6.43934 16.9393C6.64912 16.7296 6.91639 16.5867 7.20736 16.5288C7.49834 16.4709 7.79994 16.5006 8.07403 16.6142C8.34811 16.7277 8.58238 16.92 8.7472 17.1666C8.91203 17.4133 9 17.7033 9 18C9 18.3978 8.84196 18.7794 8.56066 19.0607C8.27936 19.342 7.89782 19.5 7.5 19.5ZM15.0938 17.25H10.4062C10.241 16.6047 9.86572 16.0327 9.33952 15.6243C8.81331 15.2158 8.16613 14.9941 7.5 14.9941C6.83387 14.9941 6.18669 15.2158 5.66048 15.6243C5.13428 16.0327 4.75898 16.6047 4.59375 17.25H3V14.25H16.5V15.4041C16.1551 15.6034 15.8532 15.8692 15.6118 16.186C15.3704 16.5029 15.1943 16.8646 15.0938 17.25ZM18 19.5C17.7033 19.5 17.4133 19.412 17.1666 19.2472C16.92 19.0824 16.7277 18.8481 16.6142 18.574C16.5006 18.2999 16.4709 17.9983 16.5288 17.7074C16.5867 17.4164 16.7296 17.1491 16.9393 16.9393C17.1491 16.7296 17.4164 16.5867 17.7074 16.5288C17.9983 16.4709 18.2999 16.5006 18.574 16.6142C18.8481 16.7277 19.0824 16.92 19.2472 17.1666C19.412 17.4133 19.5 17.7033 19.5 18C19.5 18.3978 19.342 18.7794 19.0607 19.0607C18.7794 19.342 18.3978 19.5 18 19.5ZM22.5 17.25H20.9062C20.739 16.6063 20.363 16.0362 19.8371 15.629C19.3112 15.2218 18.6651 15.0006 18 15V12H22.5V17.25Z" fill="#0288D1" />
                </g>
                <defs>
                  <clipPath id="clip0_2363_14247">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
          />
          <OverallStatCard
            label={t('dashboardCustomer')}
            value={customers}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18.8439 7.875C18.7065 9.78141 17.2923 11.25 15.7501 11.25C14.2079 11.25 12.7914 9.78188 12.6564 7.875C12.5157 5.89172 13.8925 4.5 15.7501 4.5C17.6078 4.5 18.9845 5.92781 18.8439 7.875Z" stroke="#FE4D4D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M15.7498 14.25C12.695 14.25 9.75731 15.7673 9.02138 18.7223C8.92388 19.1133 9.16903 19.5 9.57075 19.5H21.9293C22.3311 19.5 22.5748 19.1133 22.4787 18.7223C21.7428 15.72 18.8051 14.25 15.7498 14.25Z" stroke="#FE4D4D" stroke-width="1.5" stroke-miterlimit="10" />
                <path d="M9.37496 8.71594C9.26527 10.2384 8.12246 11.4375 6.89058 11.4375C5.65871 11.4375 4.51402 10.2389 4.40621 8.71594C4.29418 7.13203 5.40652 6 6.89058 6C8.37465 6 9.48699 7.16109 9.37496 8.71594Z" stroke="#FE4D4D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M9.65622 14.3453C8.81013 13.9577 7.87825 13.8086 6.8906 13.8086C4.4531 13.8086 2.10466 15.0203 1.51638 17.3805C1.43903 17.6927 1.63497 18.0016 1.9556 18.0016H7.21872" stroke="#FE4D4D" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            }
          />
          <OverallStatCard
            label={t('orders')}
            value={orders}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17 18C17.5304 18 18.0391 18.2107 18.4142 18.5858C18.7893 18.9609 19 19.4696 19 20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22C16.4696 22 15.9609 21.7893 15.5858 21.4142C15.2107 21.0391 15 20.5304 15 20C15 18.89 15.89 18 17 18ZM1 2H4.27L5.21 4H20C20.2652 4 20.5196 4.10536 20.7071 4.29289C20.8946 4.48043 21 4.73478 21 5C21 5.17 20.95 5.34 20.88 5.5L17.3 11.97C16.96 12.58 16.3 13 15.55 13H8.1L7.2 14.63L7.17 14.75C7.17 14.8163 7.19634 14.8799 7.24322 14.9268C7.29011 14.9737 7.3537 15 7.42 15H19V17H7C6.46957 17 5.96086 16.7893 5.58579 16.4142C5.21071 16.0391 5 15.5304 5 15C5 14.65 5.09 14.32 5.24 14.04L6.6 11.59L3 4H1V2ZM7 18C7.53043 18 8.03914 18.2107 8.41421 18.5858C8.78929 18.9609 9 19.4696 9 20C9 20.5304 8.78929 21.0391 8.41421 21.4142C8.03914 21.7893 7.53043 22 7 22C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20C5 18.89 5.89 18 7 18ZM16 11L18.78 6H6.14L8.5 11H16Z" fill="#219653" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-[#F0F0F0]" />

      {/* Bottom section: Customers Overview */}
      <div className="p-4">
        {/* Header row */}
        <div className="mb-3 flex items-center justify-between">
          <span className="font-sans text-base font-bold text-[#1A1A1A]">
            {t('customersOverview')}
          </span>
          <div className="relative">
            {/* Bug #32: <option> phải có `value` là mã cố định. Trước đây option
                không có value nên giá trị của select là NHÃN ĐÃ DỊCH — đổi ngôn
                ngữ là state thành một chuỗi không ai hiểu. */}
            <select
              value={period}
              onChange={(e) => {
                if (isQuickPeriod(e.target.value)) onPeriodChange(e.target.value);
              }}
              className="h-9 appearance-none rounded-lg border border-[#E0E0E0] bg-white pl-3 pr-8 text-sm text-[#444] focus:outline-none"
            >
              {QUICK_PERIODS.map((option) => (
                <option key={option} value={option}>
                  {t(QUICK_PERIOD_LABEL_KEYS[option])}
                </option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 5l4 4 4-4" stroke="#666" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <div className="flex items-center gap-6">
          {/* CHỈ biểu đồ chuyển động; các con số bên phải, header row (kèm
              dropdown) và khối 3 thẻ Suppliers / Customer / Orders đều đứng yên.
              TUYỆT ĐỐI KHÔNG đặt `key={period}` ở đây: remount là hai vòng bị
              dựng lại từ 0. TwoRingDonut tự morph từ giá trị đang hiển thị. */}
          <div className={cn('shrink-0', dimClass)}>
            <TwoRingDonut firstTimePercent={firstTimePercent} returnPercent={returnPercent} />
          </div>

          {/* Stats */}
          <div className={cn('flex flex-1 gap-4', dimClass)}>
            {/* First Time */}
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold text-[#1A1A1A]">{formatK(firstTime)}</span>
              <span className="text-sm font-semibold text-[#E8472A]">{t('firstTime')}</span>
              {/* Bug #25: chỉ hiện badge % khi có dữ liệu thật */}
              {hasCustomerData && firstTimeChange !== 0 && (
                <span className={cn(
                  'mt-1 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-white',
                  firstTimeChange > 0 ? 'bg-[#2E7D32]' : 'bg-[#C62828]'
                )}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={firstTimeChange < 0 ? 'rotate-180' : undefined}>
                    <path d="M5 1L1 5h2.5v4h3V5H9L5 1z" fill="white" />
                  </svg>
                  {firstTimeChange}%
                </span>
              )}
            </div>

            {/* Return */}
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold text-[#1A1A1A]">{formatK(returnCust)}</span>
              <span className="text-sm font-semibold text-[#0D9488]">{t('return')}</span>
              {/* Bug #25: chỉ hiện badge % khi có dữ liệu thật */}
              {hasCustomerData && returnChange !== 0 && (
                <span className={cn(
                  'mt-1 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-white',
                  returnChange > 0 ? 'bg-[#2E7D32]' : 'bg-[#C62828]'
                )}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={returnChange < 0 ? 'rotate-180' : undefined}>
                    <path d="M5 1L1 5h2.5v4h3V5H9L5 1z" fill="white" />
                  </svg>
                  {returnChange}%
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
